/**
 * Knowledge base — public surface.
 *
 * Consumers ask for either:
 *   - the ACTIVE ruleset's knowledge via `getActiveKnowledge()`
 *   - a SPECIFIC historical version via `getKnowledge(version)`
 *
 * Both paths return a fully-typed `AtoKnowledgeBase` object. If a section
 * is still `status: "scaffold"`, `readRate()` / `readThreshold()` will
 * refuse to hand out the number and instead return `undefined` — that
 * way half-baked scaffolds can never leak into a live calc by accident.
 *
 * `stampRuleset()` is the single helper that tax-adjacent code should
 * use to record which ruleset produced a number. It mirrors the shape
 * expected by the `TaxGuidanceLog` model on the backend.
 */

import { CURRENT_ATO_RULESET_VERSION } from "../current";
import type {
  AtoKnowledgeBase,
  AtoRateValue,
} from "./types";
import { knowledge_2026_1 } from "./2026.1";
import { knowledge_2026_2 } from "./2026.2";

/* ------------------------------------------------------------------ */
/* Version → knowledge lookup                                         */
/* ------------------------------------------------------------------ */

/**
 * Registered knowledge modules, keyed by version string. Add a new
 * entry per version — never overwrite an existing one.
 */
const KNOWLEDGE_BY_VERSION: Record<string, AtoKnowledgeBase> = {
  "2026.1": knowledge_2026_1,
  "2026.2": knowledge_2026_2,
};

/** Return the full knowledge base for a specific version. Throws if unknown. */
export function getKnowledge(version: string): AtoKnowledgeBase {
  const kb = KNOWLEDGE_BY_VERSION[version];
  if (!kb) {
    throw new Error(
      `No ATO knowledge base registered for version "${version}". ` +
        `Known: ${Object.keys(KNOWLEDGE_BY_VERSION).join(", ")}`
    );
  }
  return kb;
}

/** Convenience — the knowledge base for the currently active ruleset. */
export function getActiveKnowledge(): AtoKnowledgeBase {
  return getKnowledge(CURRENT_ATO_RULESET_VERSION);
}

/* ------------------------------------------------------------------ */
/* Safe readers                                                       */
/* Refuse to return numbers from a `status: "scaffold"` section so    */
/* placeholder zeros never sneak into a live tax calc.                */
/* ------------------------------------------------------------------ */

type RatesKey = keyof AtoKnowledgeBase["rates"];
type ThresholdsKey = keyof AtoKnowledgeBase["thresholds"];

/** Read a confirmed rate. Returns `undefined` if the section is a scaffold. */
export function readRate(
  key: Exclude<RatesKey, "status">,
  version: string = CURRENT_ATO_RULESET_VERSION
): AtoRateValue | undefined {
  const kb = getKnowledge(version);
  if (kb.rates.status !== "confirmed") return undefined;
  const value = kb.rates[key];
  return value ?? undefined;
}

/** Read a confirmed threshold. Returns `undefined` if the section is a scaffold. */
export function readThreshold(
  key: Exclude<ThresholdsKey, "status">,
  version: string = CURRENT_ATO_RULESET_VERSION
): AtoRateValue | undefined {
  const kb = getKnowledge(version);
  if (kb.thresholds.status !== "confirmed") return undefined;
  const value = kb.thresholds[key];
  return value ?? undefined;
}

/* ------------------------------------------------------------------ */
/* Ruleset stamp for tax outputs                                      */
/* Mirrors the shape stored on the backend `TaxGuidanceLog` row so a  */
/* PDF/BAS/TPAR export always knows which rules produced its numbers. */
/* ------------------------------------------------------------------ */

export interface AtoRulesetStamp {
  version: string;
  stampedAt: string; // ISO 8601 UTC
}

/**
 * Produce a ruleset stamp for the current instant. Attach the result to
 * any tax-adjacent output (BAS worksheet, TPAR draft, PDF pack, /review
 * save) so a future audit can reproduce the numbers exactly.
 *
 * We deliberately do NOT accept an override version — the stamp records
 * WHICH ruleset was active at the moment of computation, so it must
 * always come from `CURRENT_ATO_RULESET_VERSION`.
 */
export function stampRuleset(): AtoRulesetStamp {
  return {
    version: CURRENT_ATO_RULESET_VERSION,
    stampedAt: new Date().toISOString(),
  };
}

/* ------------------------------------------------------------------ */
/* Re-exports                                                          */
/* ------------------------------------------------------------------ */

export type {
  AtoKnowledgeBase,
  AtoRates,
  AtoThresholds,
  AtoReferences,
  AtoReference,
  AtoRateValue,
  AtoEffectiveWindow,
} from "./types";

export {
  AtoKnowledgeBaseSchema,
  AtoRatesSchema,
  AtoThresholdsSchema,
  AtoReferencesSchema,
  AtoReferenceSchema,
  AtoRateValueSchema,
  AtoEffectiveWindowSchema,
} from "./types";
